import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {contentFeedItemsApi} from 'common/src/modules/contentFeedItems/contentFeedItems.api';
import {loadDraftItems} from 'common/src/modules/contentFeeds/store/contentFeedPage/thunks/loadDraftItems.thunk';
import assertAiGeneratedContentFeed, {
  CONTENT_FEED_AI_TOOL_CONTEXT_ERROR,
} from '../utils/assertAiGeneratedContentFeed';
import {ContentFeedAiGeneratedSkillToolName} from '../skills/contentFeedAiGeneratedSkill.enums';
import linkMissingAssetsToContentFeed from '../utils/linkMissingAssetsToContentFeed';
import normalizeDraftColumnValues from '../utils/normalizeDraftColumnValues';
import validatePersonaColumnValues from '../utils/validatePersonaColumnValues';

const inputSchema = z.object({
  items: z
    .array(
      z.object({
        name: z.string().describe('Display name for the draft item'),
        columnValues: z
          .record(z.string())
          .optional()
          .nullable()
          .describe(
            'Map of internal columnKey (e.g. "Text1", "Image5") to value (all values strings). Use the columnKey from the feed column definitions, NOT the display name. For persona, use person ID (string). For image/video columns values use asset ID as string.',
          ),
      }),
    )
    .describe('Draft items to create'),
});

export default function createDraftItemsTool(store: CLStore) {
  async function execute({
    input: {items},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const feed = state.contentFeedPage?.feed;

    if (!assertAiGeneratedContentFeed(feed)) {
      return toolError(CONTENT_FEED_AI_TOOL_CONTEXT_ERROR);
    }

    if (!items?.length) {
      return toolError('No items provided. Specify at least one draft item to create.');
    }

    for (const item of items) {
      const personaError = validatePersonaColumnValues(feed, item.columnValues);

      if (personaError) {
        return toolError(`Item '${item.name}': ${personaError}`);
      }
    }

    const feedId = feed.id;

    const payloads = items.map((item) => ({
      name: item.name,
      columnValues: normalizeDraftColumnValues(item.columnValues ?? undefined) as
        | Record<string, unknown>
        | undefined,
    }));

    try {
      await linkMissingAssetsToContentFeed(
        store,
        feed,
        items.map((i) => ({columnValues: i.columnValues ?? null})),
      );

      const ids = await contentFeedItemsApi.createDraftItemsBatch(feedId, payloads);

      await store.dispatch(loadDraftItems({feedId, forceReload: true})).unwrap();

      return toolResult(
        `Successfully created ${ids.length} draft item(s). New draft item ID(s): ${ids.join(', ')}.`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      return toolError(`Error creating draft items: ${errorMessage}`);
    }
  }

  return tool({
    displayName: 'Creating draft items',
    name: ContentFeedAiGeneratedSkillToolName.CreateDraftItems,
    description:
      'Creates one or more draft content feed items with column values. Draft items are used before activation into published feed items.',
    execute,
    inputSchema,
  });
}
